import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { CreateOrUpdateUserAsAdmin } from "src/common/components/CreateOrUpdateUserAsAdmin/CreateOrUpdateUserAsAdmin";
import {
  getSelectedProjectGroup,
  fetchSpecificProjectGroupWithProjects,
} from "src/features/projectGroups/state/projectGroupsSlice";
import type { IUserRole } from "src/common/components/CreateOrUpdateUserAsAdmin/CreateOrUpdateUserAsAdmin";
import Spinner from "src/common/components/Loader/Spinner";

/**
 * Story 19.3: Simplified "Add Learner" Flow for Project Groups
 * Wraps CreateOrUpdateUserAsAdmin component with project-group specific context
 * - Determines userRole based on project-group auth_type
 * - Filters module permissions to only show modules in current project-group
 */
const ProjectGroupLearnersInvite = () => {
  const { id: projectGroupIdStr } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const selectedProjectGroup = useAppSelector(getSelectedProjectGroup);

  // Convert projectGroupId to number immediately for type safety
  const projectGroupId = projectGroupIdStr ? Number(projectGroupIdStr) : null;

  // Fetch and select project group data when component mounts or ID changes
  // This handles direct navigation/bookmarks when Redux state is empty
  useEffect(() => {
    if (projectGroupId && !isNaN(projectGroupId)) {
      // Fetch the project group data (which now also sets it as current - see Redux fix)
      void dispatch(fetchSpecificProjectGroupWithProjects({ projectGroupId }));
    }
  }, [dispatch, projectGroupId]);

  // Validate projectGroupId from URL params
  if (!projectGroupId || isNaN(projectGroupId)) {
    return <div data-testid="error-invalid-id">Invalid project group ID</div>;
  }

  // Wait for the CORRECT project group data to load before rendering
  // The fetchSpecificProjectGroupWithProjects action sets state.current, so this check ensures
  // we don't render with stale data from a previously selected project group
  const isCorrectProjectGroupLoaded =
    selectedProjectGroup &&
    selectedProjectGroup.auth_type &&
    Number(selectedProjectGroup.id) === projectGroupId;

  if (!isCorrectProjectGroupLoaded) {
    return (
      <div data-testid="loading-spinner">
        <Spinner />
      </div>
    );
  }

  // Determine userRole based on project-group auth_type
  const userRole: IUserRole = (() => {
    const authType = selectedProjectGroup.auth_type;
    switch (authType) {
      case "simplified":
        return "simplifiedEnduser";
      case "classic":
      case "authenticated":
      default:
        return "enduser";
    }
  })();

  return (
    <div data-testid="project-group-learners-invite" className="h-full">
      <CreateOrUpdateUserAsAdmin
        mode="create"
        userRole={userRole}
        projectGroupId={projectGroupId}
      />
    </div>
  );
};

export default ProjectGroupLearnersInvite;
